import { Form } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { lazy, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Margin, Padding } from 'components/ui'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { Button, Flex, FormItem, Paragraph, Title } from 'components/uiKit'
import { useResize } from 'utils/hooks/useResize'
import { DATE_FORMAT_DEFAULT } from 'shared/constants/values'
import { BaseOption } from 'shared/types/types'

import { OldPassportPhoto } from '../../components/PersonalDataSteps/PassportPhotoStep/OldPassportPhoto'
import { PassportPhotoStep } from '../../components/PersonalDataSteps/PassportPhotoStep/PassportPhotoStep'
import { OldIdentificationForm } from '../../components/PersonalDataSteps/PassportStep/PassportForm/OldIdentificationForm'
import { OldPassportForm } from '../../components/PersonalDataSteps/PassportStep/PassportForm/OldPassportForm'
import { PassportStep } from '../../components/PersonalDataSteps/PassportStep/PassportStep'
import {
  citizenshipOptions,
  countriesWithMultipleDocuments,
  documentsTypeOptions,
  identificationCardLabels,
} from '../../data'
import { Citizenship, DocumentType } from '../../types'

const EditPassportPage = observer(() => {
  const { profileUiStore: uiStore } = useStore()
  const { companyDetails } = uiStore
  const [isVisibleAskForm, setVisibleAskForm] = useState<boolean>(true)
  const [isVisibleOldForm, setVisibleOldForm] = useState<boolean>(false)
  const { isMobile } = useResize()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const isPassport = uiStore.companyDetails.documentType === DocumentType.passport
  const isCountryWithMultipleDocuments = countriesWithMultipleDocuments.some(
    (country) => country === companyDetails.selectedCitizenship.value,
  )

  const isDisabledButtonUpdateOldForm = isPassport
    ? uiStore.validateStore.isInvalidOldPassport
    : uiStore.validateStore.isInvalidOldIdentification
  const isDisabledButtonUpdateNewForm =
    (isPassport ? uiStore.validateStore.isInvalidPassportForm : uiStore.validateStore.isInvalidIdentificationForm) ||
    uiStore.companyDetails.isInvalidDocumentPhoto ||
    uiStore.companyDetails.isInvalidWithDocumentPhoto

  const isDisabledButtonUpdate = isVisibleOldForm ? isDisabledButtonUpdateOldForm : isDisabledButtonUpdateNewForm

  useEffect(() => {
    return () => {
      setVisibleAskForm(true)
      setVisibleOldForm(false)
      companyDetails.clearCompanyInfo()
      form.resetFields()
    }
  }, [])

  useEffect(() => {
    if (isVisibleAskForm) {
      uiStore.createEditData()
    }
  }, [isVisibleAskForm])

  const backButtonHandler = () => {
    navigate(-1)
  }

  const setDocumentType = (option: BaseOption<DocumentType>) => {
    companyDetails.setDocumentType(option.value)
  }

  const handleChangeCitizenship = (item: BaseOption<Citizenship>) => {
    companyDetails.setSelectedCitizenship(item)

    if (item.value === Citizenship.TJ || item.value === Citizenship.GE) {
      companyDetails.setDocumentType(DocumentType.identification)
      return
    }

    companyDetails.setDocumentType(DocumentType.passport)
  }

  const getPassportData = async () => {
    const result = await uiStore.apiStore.getDocument()
    if (result === null) {
      setVisibleOldForm(true)
    }
    setVisibleAskForm(false)
  }

  const updateOldPassport = async () => {
    const result = await uiStore.apiStore.updateOldPassport()
    if (result) {
      uiStore.companyDetails.clearCompanyInfo()
      navigate(-1)
      form.resetFields()
    }
  }

  const updateNewForm = () => {
    uiStore.apiStore.updateOnlyPassport().then(() => {
      navigate(-1)
    })
  }

  const handleUpdatePassport = async () => {
    if (isVisibleOldForm) {
      updateOldPassport()
    } else {
      updateNewForm()
    }
  }

  const handleCloseForm = () => {
    setVisibleAskForm(true)
    setVisibleOldForm(false)
    form.resetFields()
    uiStore.companyDetails.clearCompanyInfo()
  }

  const getDocumentTypeOptions = () => {
    const selectedCitizenship = companyDetails.selectedCitizenship
    return documentsTypeOptions.map((option) => {
      if (option.value === DocumentType.identification) {
        return {
          label: identificationCardLabels[selectedCitizenship.value],
          value: option.value,
        }
      }
      return option
    })
  }

  useEffect(() => {
    form.setFieldValue('passport', companyDetails.passport)
    form.setFieldValue('nationality', companyDetails.nationality)
    form.setFieldValue('identificationNumber', companyDetails.identificationNumber)
    form.setFieldValue('passportIssued', companyDetails.passportIssued)
    form.setFieldValue('registrationAddress', companyDetails.registrationAddress)
    form.setFieldValue('departmentCode', companyDetails.departmentCode)
    form.setFieldValue('placeOfBirth', companyDetails.placeOfBirth)
    form.setFieldValue('documentNumber', companyDetails.documentNumber)
    form.setFieldValue('iin', companyDetails.iin)
    form.setFieldValue('socialCardNumber', companyDetails.socialCardNumber)
    form.setFieldValue('personalNumber', companyDetails.personalNumber)
    form.setFieldValue('innCitizen', companyDetails.innCitizen)
    form.setFieldValue('permanentResidenceAddress', companyDetails.permanentResidenceAddress)
    form.setFieldValue(
      'dateOfIssue',
      companyDetails.dateOfIssue ? dayjs(companyDetails.dateOfIssue, DATE_FORMAT_DEFAULT) : null,
    )
    form.setFieldValue(
      'expirationDate',
      companyDetails.expirationDate ? dayjs(companyDetails.expirationDate, DATE_FORMAT_DEFAULT) : null,
    )
    form.setFieldValue(
      'dateOfBirthday',
      companyDetails.dateOfBirthday ? dayjs(companyDetails.dateOfBirthday, DATE_FORMAT_DEFAULT) : null,
    )
  }, [
    companyDetails.passport,
    companyDetails.nationality,
    companyDetails.identificationNumber,
    companyDetails.dateOfIssue,
    companyDetails.expirationDate,
    companyDetails.dateOfBirthday,
    companyDetails.passportIssued,
    companyDetails.registrationAddress,
    companyDetails.departmentCode,
    companyDetails.placeOfBirth,
    companyDetails.documentNumber,
    companyDetails.iin,
    companyDetails.socialCardNumber,
    companyDetails.personalNumber,
    companyDetails.innCitizen,
    companyDetails.permanentResidenceAddress,
  ])

  return (
    <ContainerLK>
      <Padding py={32}>
        <Margin mt={isMobile ? 0 : 50} mb={isMobile ? 40 : 80}>
          <Flex gap={16} align="center">
            <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={backButtonHandler} />
            <Title>Редактирование паспортных данных</Title>
          </Flex>
        </Margin>

        {isVisibleAskForm && (
          <Form layout="vertical" style={{ maxWidth: 600, marginTop: 50 }}>
            <Paragraph>Выберите параметры указанные при регистрации</Paragraph>
            <FormItem label={'Гражданство'}>
              <ReactSelect
                placeholder={'Выберите гражданство'}
                options={citizenshipOptions}
                value={citizenshipOptions.find((option) => option.value === companyDetails.selectedCitizenship.value)}
                onChange={(citizenship) => handleChangeCitizenship(citizenship as BaseOption<Citizenship>)}
              />
            </FormItem>
            {isCountryWithMultipleDocuments && (
              <FormItem label={'Тип документа'}>
                <ReactSelect
                  placeholder="Выберите тип документа"
                  value={getDocumentTypeOptions().find((option) => option.value === companyDetails.documentType)}
                  options={getDocumentTypeOptions()}
                  onChange={(option) => setDocumentType(option as BaseOption<DocumentType>)}
                />
              </FormItem>
            )}

            <Button onClick={getPassportData} loading={uiStore.loadingPassportData}>
              Получить форму для редактирования
            </Button>
          </Form>
        )}

        {!isVisibleAskForm && (
          <>
            {isVisibleOldForm ? (
              <>
                <Flex vertical style={{ maxWidth: 600 }}>
                  <Title level={3}>{isPassport ? 'Паспортные данные' : 'Удостоверение личности'}</Title>
                  <Form form={form} layout="vertical" style={{ maxWidth: 600, marginTop: 50 }}>
                    {isPassport ? (
                      <OldPassportForm uiStore={companyDetails} />
                    ) : (
                      <OldIdentificationForm uiStore={companyDetails} />
                    )}
                    <OldPassportPhoto companyDetails={companyDetails} />
                  </Form>
                </Flex>
              </>
            ) : (
              <>
                <PassportStep companyDetails={uiStore.companyDetails} profile={uiStore.profile} isEdit />
                <PassportPhotoStep companyDetails={uiStore.companyDetails} isEdit />
              </>
            )}

            <Flex gap={20}>
              <Button type={'default'} onClick={handleCloseForm}>
                Назад
              </Button>
              <Button onClick={handleUpdatePassport} disabled={isDisabledButtonUpdate} loading={uiStore.isSendingData}>
                Обновить
              </Button>
            </Flex>
          </>
        )}
      </Padding>
    </ContainerLK>
  )
})

export default EditPassportPage

export const EditPassportPageAsync = lazy(() => import('./EditPassportPage'))
